import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrgProject } from '../contexts/OrgProjectContext';
import SCMWizard from '../components/SCMWizard';
import PageLayout from '../components/PageLayout';
import { apiFetch } from '../apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  navy:       '#0F172A',
  navyMid:    '#1E293B',
  navyLight:  '#334155',
  blue:       '#3B82F6',
  blueLt:     '#DBEAFE',
  green:      '#10B981',
  white:      '#FFFFFF',
  slate:      '#64748B',
  slateLight: '#94A3B8',
  border:     '#E2E8F0',
  bg:         '#F8FAFC',
  text:       '#1E293B',
  textMid:    '#475569',
  red:        '#EF4444',
  redLt:      '#FEE2E2',
  yellow:     '#F59E0B',
  yellowLt:   '#FEF3C7',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function sourceTypeLabel(st) {
  switch (st) {
    case 'filesystem': return 'Filesystem';
    case 'git_repo': return 'Git Repo';
    case 's3_bucket': return 'S3 Bucket';
    default: return st || '—';
  }
}

function targetCloudLabel(tc) {
  switch (tc) {
    case 'aws': return 'AWS';
    case 'azure': return 'Azure';
    case 'gcp': return 'GCP';
    default: return '—';
  }
}

// ─── Validation ───────────────────────────────────────────────────────────────

function validateName(name) {
  if (!name || name.trim().length === 0) return 'Name is required';
  if (name.length > 100) return 'Name must be 100 characters or fewer';
  return null;
}

function validateDescription(desc) {
  if (desc && desc.length > 500) return 'Description must be 500 characters or fewer';
  return null;
}

function validateSourceConfig(sourceType, config) {
  const errors = {};
  switch (sourceType) {
    case 'filesystem':
      if (!config.base_path || config.base_path.trim().length === 0) {
        errors.base_path = 'Base path is required';
      }
      break;
    case 'git_repo':
      if (!config.scm_connection_id) {
        errors.scm_connection_id = 'SCM connection is required';
      }
      if (config.repo_filter && config.repo_filter.length > 255) {
        errors.repo_filter = 'Repository filter must be 255 characters or fewer';
      }
      break;
    case 's3_bucket':
      if (!config.bucket_name || config.bucket_name.trim().length === 0) {
        errors.bucket_name = 'Bucket name is required';
      } else if (config.bucket_name.length > 63) {
        errors.bucket_name = 'Bucket name must be 63 characters or fewer';
      }
      if (config.prefix && config.prefix.length > 1024) {
        errors.prefix = 'Prefix must be 1024 characters or fewer';
      }
      break;
    default:
      break;
  }
  return errors;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProjectManagement() {
  const navigate = useNavigate();
  const { orgId: routeOrgId } = useParams();
  const { orgId, syncFromRouteParams } = useOrgProject();

  // Sync context from route params on mount
  useEffect(() => {
    if (routeOrgId) {
      syncFromRouteParams(routeOrgId);
    }
  }, [routeOrgId, syncFromRouteParams]);

  // SCM Wizard state
  const [showSCMWizard, setShowSCMWizard] = useState(false);

  // List state
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false); // 'create' | 'edit' | false
  const [editingProject, setEditingProject] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSourceType, setFormSourceType] = useState('filesystem');
  const [formSourceConfig, setFormSourceConfig] = useState({});
  const [formTargetCloud, setFormTargetCloud] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formApiError, setFormApiError] = useState(null);

  // SCM connections for git_repo source type
  const [scmConnections, setScmConnections] = useState([]);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  // ── Fetch projects ────────────────────────────────────────────────────────
  const fetchProjects = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setListError(null);
    try {
      const res = await apiFetch(`/api/orgs/${orgId}/projects`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load projects (${res.status})`);
      }
      const data = await res.json();
      const projectList = Array.isArray(data) ? data : (data.data || data.results || data.items || []);
      // Sort by name ascending
      projectList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProjects(projectList);
    } catch (err) {
      setListError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // ── Fetch SCM connections ─────────────────────────────────────────────────
  const fetchSCMConnections = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await apiFetch(`/api/orgs/${orgId}/scm-connections`);
      if (res.ok) {
        const data = await res.json();
        const connections = Array.isArray(data) ? data : (data.data || data.results || data.items || []);
        setScmConnections(connections);
      }
    } catch {
      // Silently fail — SCM connections are optional
    }
  }, [orgId]);

  useEffect(() => {
    fetchSCMConnections();
  }, [fetchSCMConnections]);

  // ── Toast auto-dismiss ────────────────────────────────────────────────────
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ── Form handlers ─────────────────────────────────────────────────────────
  const openCreateForm = () => {
    setShowForm('create');
    setEditingProject(null);
    setFormName('');
    setFormDescription('');
    setFormSourceType('filesystem');
    setFormSourceConfig({});
    setFormTargetCloud('');
    setFormErrors({});
    setFormApiError(null);
  };

  const openEditForm = (project) => {
    setShowForm('edit');
    setEditingProject(project);
    setFormName(project.name || '');
    setFormDescription(project.description || '');
    setFormSourceType(project.source_type || 'filesystem');
    // Parse source_config — it may be a JSON string or object
    let config = project.source_config || {};
    if (typeof config === 'string') {
      try { config = JSON.parse(config); } catch { config = {}; }
    }
    setFormSourceConfig(config);
    setFormTargetCloud(project.target_cloud || '');
    setFormErrors({});
    setFormApiError(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingProject(null);
    setFormErrors({});
    setFormApiError(null);
  };

  // Clear source-specific fields when source_type changes
  const handleSourceTypeChange = (newType) => {
    setFormSourceType(newType);
    setFormSourceConfig({});
    // Clear source-specific errors
    setFormErrors(prev => {
      const { base_path, scm_connection_id, repo_filter, bucket_name, prefix, ...rest } = prev;
      return rest;
    });
  };

  const handleSourceConfigChange = (field, value) => {
    setFormSourceConfig(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setFormErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormApiError(null);

    // Validate name and description
    const nameErr = validateName(formName);
    const descErr = validateDescription(formDescription);
    const sourceErrors = validateSourceConfig(formSourceType, formSourceConfig);
    const allErrors = {};
    if (nameErr) allErrors.name = nameErr;
    if (descErr) allErrors.description = descErr;
    Object.assign(allErrors, sourceErrors);

    if (Object.keys(allErrors).length > 0) {
      setFormErrors(allErrors);
      return;
    }
    setFormErrors({});
    setFormSubmitting(true);

    try {
      const body = {
        name: formName.trim(),
        description: formDescription.trim(),
        source_type: formSourceType,
        source_config: formSourceConfig,
        target_cloud: formTargetCloud || null,
      };
      const isEdit = showForm === 'edit' && editingProject;
      const url = isEdit
        ? `/api/orgs/${orgId}/projects/${editingProject.id}`
        : `/api/orgs/${orgId}/projects`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        if (errBody.code === 'DUPLICATE_FIELD' || (errBody.error && errBody.error.toLowerCase().includes('already'))) {
          setFormErrors(prev => ({ ...prev, name: 'Project name must be unique within this organisation' }));
          return;
        }
        if (res.status >= 400 && res.status < 500) {
          setFormApiError(errBody.error || 'Validation error');
        } else {
          setToast({ type: 'error', message: errBody.error || `Server error (${res.status})` });
        }
        return;
      }

      closeForm();
      setToast({ type: 'success', message: isEdit ? 'Project updated' : 'Project created' });
      fetchProjects();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Network error' });
    } finally {
      setFormSubmitting(false);
    }
  };

  // ── Delete handlers ───────────────────────────────────────────────────────
  const confirmDelete = (project) => {
    setDeleteTarget(project);
    setDeleteError(null);
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);

    try {
      const res = await apiFetch(`/api/orgs/${orgId}/projects/${deleteTarget.id}`, { method: 'DELETE' });

      if (res.status === 409) {
        const errBody = await res.json().catch(() => ({}));
        setDeleteError(errBody.error || 'Cannot delete: project has active pipeline runs');
        return;
      }

      if (!res.ok && res.status !== 204) {
        const errBody = await res.json().catch(() => ({}));
        if (res.status >= 500) {
          setToast({ type: 'error', message: errBody.error || `Failed to delete (${res.status})` });
        } else {
          setDeleteError(errBody.error || `Failed to delete (${res.status})`);
        }
        return;
      }

      setDeleteTarget(null);
      setToast({ type: 'success', message: 'Project deleted' });
      fetchProjects();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Network error' });
    } finally {
      setDeleting(false);
    }
  };

  // ── Inline validation on change ───────────────────────────────────────────
  const handleNameChange = (val) => {
    setFormName(val);
    const err = validateName(val);
    setFormErrors(prev => ({ ...prev, name: err }));
    if (formApiError) setFormApiError(null);
  };

  const handleDescriptionChange = (val) => {
    setFormDescription(val);
    const err = validateDescription(val);
    setFormErrors(prev => ({ ...prev, description: err }));
  };

  // ── Source-type specific form fields ──────────────────────────────────────
  const renderSourceFields = () => {
    switch (formSourceType) {
      case 'filesystem':
        return (
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="proj-base-path">
              Base Path <span style={styles.required}>*</span>
            </label>
            <input
              id="proj-base-path"
              type="text"
              value={formSourceConfig.base_path || ''}
              onChange={(e) => handleSourceConfigChange('base_path', e.target.value)}
              placeholder="/path/to/applications"
              style={{
                ...styles.input,
                borderColor: formErrors.base_path ? C.red : C.border,
              }}
              aria-invalid={!!formErrors.base_path}
              aria-describedby={formErrors.base_path ? 'proj-base-path-error' : undefined}
            />
            {formErrors.base_path && (
              <span id="proj-base-path-error" style={styles.fieldError}>{formErrors.base_path}</span>
            )}
          </div>
        );

      case 'git_repo':
        return (
          <>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="proj-scm-connection">
                SCM Connection <span style={styles.required}>*</span>
              </label>
              <select
                id="proj-scm-connection"
                value={formSourceConfig.scm_connection_id || ''}
                onChange={(e) => handleSourceConfigChange('scm_connection_id', e.target.value)}
                style={{
                  ...styles.input,
                  borderColor: formErrors.scm_connection_id ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.scm_connection_id}
                aria-describedby={formErrors.scm_connection_id ? 'proj-scm-error' : undefined}
              >
                <option value="">Select a connection…</option>
                {scmConnections.map(conn => (
                  <option key={conn.id} value={conn.id}>
                    {conn.connection_name} ({conn.provider} — {conn.scm_org_name})
                  </option>
                ))}
              </select>
              {formErrors.scm_connection_id && (
                <span id="proj-scm-error" style={styles.fieldError}>{formErrors.scm_connection_id}</span>
              )}
              {scmConnections.length === 0 ? (
                <span style={styles.hintText}>
                  No SCM connections configured for this organisation.{' '}
                  <button
                    type="button"
                    onClick={() => setShowSCMWizard(true)}
                    style={{
                      background: 'none', border: 'none', color: C.blue, cursor: 'pointer',
                      fontSize: 'inherit', fontWeight: 600, padding: 0, textDecoration: 'underline',
                    }}
                  >
                    Create one now
                  </button>
                </span>
              ) : (
                <span style={styles.hintText}>
                  <button
                    type="button"
                    onClick={() => setShowSCMWizard(true)}
                    style={{
                      background: 'none', border: 'none', color: C.blue, cursor: 'pointer',
                      fontSize: 'inherit', fontWeight: 600, padding: 0, textDecoration: 'underline',
                    }}
                  >
                    + Add new SCM connection
                  </button>
                </span>
              )}
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="proj-repo-filter">
                Repository Filter
              </label>
              <input
                id="proj-repo-filter"
                type="text"
                value={formSourceConfig.repo_filter || ''}
                onChange={(e) => handleSourceConfigChange('repo_filter', e.target.value)}
                maxLength={255}
                placeholder="Optional filter pattern (e.g. legacy-*)"
                style={{
                  ...styles.input,
                  borderColor: formErrors.repo_filter ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.repo_filter}
                aria-describedby={formErrors.repo_filter ? 'proj-filter-error' : undefined}
              />
              {formErrors.repo_filter && (
                <span id="proj-filter-error" style={styles.fieldError}>{formErrors.repo_filter}</span>
              )}
              <span style={styles.charCount}>{(formSourceConfig.repo_filter || '').length}/255</span>
            </div>
          </>
        );

      case 's3_bucket':
        return (
          <>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="proj-bucket-name">
                Bucket Name <span style={styles.required}>*</span>
              </label>
              <input
                id="proj-bucket-name"
                type="text"
                value={formSourceConfig.bucket_name || ''}
                onChange={(e) => handleSourceConfigChange('bucket_name', e.target.value)}
                maxLength={63}
                placeholder="my-app-bucket"
                style={{
                  ...styles.input,
                  borderColor: formErrors.bucket_name ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.bucket_name}
                aria-describedby={formErrors.bucket_name ? 'proj-bucket-error' : undefined}
              />
              {formErrors.bucket_name && (
                <span id="proj-bucket-error" style={styles.fieldError}>{formErrors.bucket_name}</span>
              )}
              <span style={styles.charCount}>{(formSourceConfig.bucket_name || '').length}/63</span>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="proj-prefix">
                Prefix
              </label>
              <input
                id="proj-prefix"
                type="text"
                value={formSourceConfig.prefix || ''}
                onChange={(e) => handleSourceConfigChange('prefix', e.target.value)}
                maxLength={1024}
                placeholder="Optional S3 key prefix"
                style={{
                  ...styles.input,
                  borderColor: formErrors.prefix ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.prefix}
                aria-describedby={formErrors.prefix ? 'proj-prefix-error' : undefined}
              />
              {formErrors.prefix && (
                <span id="proj-prefix-error" style={styles.fieldError}>{formErrors.prefix}</span>
              )}
              <span style={styles.charCount}>{(formSourceConfig.prefix || '').length}/1024</span>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PageLayout>
    <div style={styles.container}>
      {/* Toast */}
      {toast && (
        <div style={{
          ...styles.toast,
          background: toast.type === 'success' ? C.green : C.red,
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Projects</h1>
          <p style={styles.subtitle}>Manage projects within your organisation</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={() => navigate(`/orgs/${orgId}/scm-connections`)}
            style={{
              padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: `1px solid ${C.border}`, background: C.white, color: C.text,
              cursor: 'pointer',
            }}
          >
            🔗 SCM Connections
          </button>
          <button onClick={openCreateForm} style={styles.createBtn}>
            + New Project
          </button>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>
            {showForm === 'edit' ? 'Edit Project' : 'Create Project'}
          </h2>
          <form onSubmit={handleFormSubmit}>
            {/* Name field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="proj-name">
                Name <span style={styles.required}>*</span>
              </label>
              <input
                id="proj-name"
                type="text"
                value={formName}
                onChange={(e) => handleNameChange(e.target.value)}
                maxLength={100}
                placeholder="Project name"
                style={{
                  ...styles.input,
                  borderColor: formErrors.name ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.name}
                aria-describedby={formErrors.name ? 'proj-name-error' : undefined}
              />
              {formErrors.name && (
                <span id="proj-name-error" style={styles.fieldError}>{formErrors.name}</span>
              )}
              <span style={styles.charCount}>{formName.length}/100</span>
            </div>

            {/* Description field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="proj-desc">Description</label>
              <textarea
                id="proj-desc"
                value={formDescription}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                maxLength={500}
                placeholder="Optional description"
                rows={3}
                style={{
                  ...styles.textarea,
                  borderColor: formErrors.description ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.description}
                aria-describedby={formErrors.description ? 'proj-desc-error' : undefined}
              />
              {formErrors.description && (
                <span id="proj-desc-error" style={styles.fieldError}>{formErrors.description}</span>
              )}
              <span style={styles.charCount}>{formDescription.length}/500</span>
            </div>

            {/* Source Type selector */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="proj-source-type">
                Source Type <span style={styles.required}>*</span>
              </label>
              <select
                id="proj-source-type"
                value={formSourceType}
                onChange={(e) => handleSourceTypeChange(e.target.value)}
                style={styles.input}
              >
                <option value="filesystem">Filesystem</option>
                <option value="git_repo">Git Repository</option>
                <option value="s3_bucket">S3 Bucket</option>
              </select>
            </div>

            {/* Target Cloud selector */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="proj-target-cloud">
                Target Cloud
              </label>
              <select
                id="proj-target-cloud"
                value={formTargetCloud}
                onChange={(e) => setFormTargetCloud(e.target.value)}
                style={styles.input}
              >
                <option value="">— Not set —</option>
                <option value="aws">Amazon Web Services (AWS)</option>
                <option value="azure">Microsoft Azure</option>
                <option value="gcp">Google Cloud Platform (GCP)</option>
              </select>
              <span style={styles.hintText}>
                Cloud platform for migration recommendations and IaC generation
              </span>
            </div>

            {/* Dynamic source-type-specific fields */}
            {renderSourceFields()}

            {/* API error */}
            {formApiError && (
              <div style={styles.apiError} role="alert">
                {formApiError}
              </div>
            )}

            {/* Actions */}
            <div style={styles.formActions}>
              <button type="button" onClick={closeForm} style={styles.cancelBtn}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={formSubmitting}
                style={{
                  ...styles.submitBtn,
                  opacity: formSubmitting ? 0.6 : 1,
                  cursor: formSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {formSubmitting
                  ? 'Saving…'
                  : showForm === 'edit' ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List error */}
      {listError && (
        <div style={styles.listError} role="alert">
          {listError}
          <button onClick={() => fetchProjects()} style={styles.retryBtn}>Retry</button>
        </div>
      )}

      {/* Project table */}
      {loading ? (
        <div style={styles.loadingState}>Loading projects…</div>
      ) : projects.length === 0 && !listError ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No projects yet.</p>
          <button onClick={openCreateForm} style={styles.emptyBtn}>Create your first project</button>
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Source Type</th>
                <th style={styles.th}>Target Cloud</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Apps</th>
                <th style={styles.th}>Last Run</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(project => (
                <tr key={project.id} style={styles.tr}>
                  <td style={styles.td}>
                    <span style={styles.projectNameCell}>{project.name}</span>
                    {project.description && (
                      <span style={styles.projectDescCell}>{project.description}</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.sourceTypeBadge}>
                      {sourceTypeLabel(project.source_type)}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.targetCloudBadge}>
                      {targetCloudLabel(project.target_cloud)}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={styles.appCountBadge}>
                      {project.app_count ?? project.application_count ?? 0}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.timestampCell}>
                      {formatTimestamp(project.last_run_at || project.last_pipeline_run)}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button
                      onClick={() => navigate(`/orgs/${orgId}/projects/${project.id}/apps`)}
                      style={styles.actionBtn}
                      aria-label={`View apps in ${project.name}`}
                    >
                      Apps
                    </button>
                    <button
                      onClick={() => openEditForm(project)}
                      style={styles.actionBtn}
                      aria-label={`Edit ${project.name}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => confirmDelete(project)}
                      style={styles.deleteBtn}
                      aria-label={`Delete ${project.name}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="delete-dialog-title">
          <div style={styles.dialog}>
            <h3 id="delete-dialog-title" style={styles.dialogTitle}>Delete Project</h3>
            <p style={styles.dialogText}>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
              This will also remove all associated applications. This action cannot be undone.
            </p>
            {deleteError && (
              <div style={styles.dialogError} role="alert">{deleteError}</div>
            )}
            <div style={styles.dialogActions}>
              <button onClick={cancelDelete} style={styles.cancelBtn}>Cancel</button>
              <button
                onClick={executeDelete}
                disabled={deleting}
                style={{
                  ...styles.confirmDeleteBtn,
                  opacity: deleting ? 0.6 : 1,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SCM Wizard Modal */}
      <SCMWizard
        open={showSCMWizard}
        onClose={() => setShowSCMWizard(false)}
        onCreated={() => {
          setShowSCMWizard(false);
          fetchSCMConnections();
        }}
      />
    </div>
    </PageLayout>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  container: {
    padding: '24px 32px',
    maxWidth: 960,
    margin: '0 auto',
    position: 'relative',
  },
  toast: {
    position: 'fixed',
    top: 20,
    right: 20,
    padding: '10px 20px',
    borderRadius: 8,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    zIndex: 10001,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: C.text,
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: C.slate,
    margin: '4px 0 0',
  },
  createBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(59,130,246,0.3)',
  },
  // Form
  formCard: {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: '20px 24px',
    marginBottom: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  formTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 16px',
  },
  fieldGroup: {
    marginBottom: 16,
    position: 'relative',
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: C.textMid,
    marginBottom: 4,
  },
  required: {
    color: C.red,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    fontSize: 13,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    outline: 'none',
    color: C.text,
    background: C.white,
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '9px 12px',
    fontSize: 13,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    outline: 'none',
    color: C.text,
    background: C.white,
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  fieldError: {
    display: 'block',
    fontSize: 11,
    color: C.red,
    marginTop: 4,
  },
  charCount: {
    position: 'absolute',
    right: 0,
    top: 0,
    fontSize: 10,
    color: C.slateLight,
  },
  hintText: {
    display: 'block',
    fontSize: 11,
    color: C.slateLight,
    marginTop: 4,
    fontStyle: 'italic',
  },
  apiError: {
    padding: '10px 14px',
    borderRadius: 6,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 16,
    border: `1px solid ${C.red}20`,
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelBtn: {
    padding: '8px 16px',
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.textMid,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '8px 18px',
    borderRadius: 6,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  // List
  listError: {
    padding: '12px 16px',
    borderRadius: 8,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  retryBtn: {
    padding: '4px 12px',
    borderRadius: 4,
    border: `1px solid ${C.red}`,
    background: 'transparent',
    color: C.red,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px 0',
    color: C.slate,
    fontSize: 13,
    fontStyle: 'italic',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 0',
  },
  emptyText: {
    color: C.slate,
    fontSize: 14,
    margin: '0 0 12px',
  },
  emptyBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.white,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: C.slate,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: `1px solid ${C.border}`,
    background: C.bg,
  },
  tr: {
    borderBottom: `1px solid ${C.border}`,
  },
  td: {
    padding: '12px 16px',
    verticalAlign: 'middle',
  },
  projectNameCell: {
    display: 'block',
    fontWeight: 600,
    color: C.text,
    fontSize: 13,
  },
  projectDescCell: {
    display: 'block',
    fontSize: 11,
    color: C.slate,
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 300,
  },
  sourceTypeBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 99,
    background: C.yellowLt,
    color: C.yellow,
    fontSize: 11,
    fontWeight: 700,
  },
  targetCloudBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 99,
    background: C.blueLt,
    color: C.blue,
    fontSize: 11,
    fontWeight: 700,
  },
  appCountBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 99,
    background: C.blueLt,
    color: C.blue,
    fontSize: 12,
    fontWeight: 700,
  },
  timestampCell: {
    fontSize: 12,
    color: C.slate,
  },
  actionBtn: {
    padding: '5px 12px',
    borderRadius: 5,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.textMid,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: 6,
  },
  deleteBtn: {
    padding: '5px 12px',
    borderRadius: 5,
    border: `1px solid ${C.red}40`,
    background: 'transparent',
    color: C.red,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  // Delete dialog
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10002,
  },
  dialog: {
    background: C.white,
    borderRadius: 12,
    padding: '24px 28px',
    maxWidth: 420,
    width: '90%',
    boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
  },
  dialogTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 12px',
  },
  dialogText: {
    fontSize: 13,
    color: C.textMid,
    margin: '0 0 16px',
    lineHeight: 1.5,
  },
  dialogError: {
    padding: '10px 14px',
    borderRadius: 6,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 16,
    border: `1px solid ${C.red}20`,
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  confirmDeleteBtn: {
    padding: '8px 18px',
    borderRadius: 6,
    border: 'none',
    background: C.red,
    color: C.white,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
};
